import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import type { StrapiQuotationRow } from '../../../api/strapiCrm';
import { queryKeys } from '../../../query/queryKeys';
import { useUpdateCustomerMutation } from '../../../query/hooks';

type CustomerAddress = NonNullable<NonNullable<StrapiQuotationRow['customer']>>;

function asText(value: string | null | undefined): string {
  return value ?? '';
}

function nz(value: string): string | null {
  const t = value.trim();
  return t === '' ? null : t;
}

type Props = {
  customer: CustomerAddress | null | undefined;
  jwt: string | null;
  workOrderDocumentId: string;
};

export function SurveyCustomerAddressForm({ customer, jwt, workOrderDocumentId }: Props) {
  const qc = useQueryClient();
  const updateMut = useUpdateCustomerMutation(jwt);
  const [name, setName] = useState(() => asText(customer?.name));
  const [address, setAddress] = useState(() => asText(customer?.address));
  const [city, setCity] = useState(() => asText(customer?.city));
  const [province, setProvince] = useState(() => asText(customer?.province));
  const [postalCode, setPostalCode] = useState(() => asText(customer?.postal_code));
  const [contactName, setContactName] = useState(() => asText(customer?.contact_name));
  const [jobTitle, setJobTitle] = useState(() => asText(customer?.job_title));
  const [saveOk, setSaveOk] = useState(false);
  const nameTrimmed = name.trim();
  const nameMissing = nameTrimmed === '';

  useEffect(() => {
    setSaveOk(false);
  }, [customer?.documentId]);

  useEffect(() => {
    setName(asText(customer?.name));
    setAddress(asText(customer?.address));
    setCity(asText(customer?.city));
    setProvince(asText(customer?.province));
    setPostalCode(asText(customer?.postal_code));
    setContactName(asText(customer?.contact_name));
    setJobTitle(asText(customer?.job_title));
  }, [
    customer?.documentId,
    customer?.name,
    customer?.address,
    customer?.city,
    customer?.province,
    customer?.postal_code,
    customer?.contact_name,
    customer?.job_title,
  ]);

  const dirty = useMemo(() => {
    if (!customer?.documentId) return false;
    return (
      name !== asText(customer.name) ||
      address !== asText(customer.address) ||
      city !== asText(customer.city) ||
      province !== asText(customer.province) ||
      postalCode !== asText(customer.postal_code) ||
      contactName !== asText(customer.contact_name) ||
      jobTitle !== asText(customer.job_title)
    );
  }, [customer, name, address, city, province, postalCode, contactName, jobTitle]);

  if (!customer?.documentId) {
    return (
      <Alert severity="info">
        Pelanggan tidak terhubung ke work order ini. Data pelanggan tidak dapat diubah.
      </Alert>
    );
  }

  async function onSave() {
    if (!customer?.documentId || nameTrimmed === '') return;
    setSaveOk(false);
    try {
      await updateMut.mutateAsync({
        documentId: customer.documentId,
        data: {
          name: nameTrimmed,
          address: nz(address),
          city: nz(city),
          province: nz(province),
          postal_code: nz(postalCode),
          contact_name: nz(contactName),
          job_title: nz(jobTitle),
        },
      });
      await qc.invalidateQueries({ queryKey: queryKeys.workOrderDetail(jwt, workOrderDocumentId) });
      await qc.invalidateQueries({
        queryKey: ['strapi', 'samplingPlansByWorkOrder', jwt, workOrderDocumentId],
      });
      await qc.invalidateQueries({ queryKey: queryKeys.customerDetail(jwt, customer.documentId) });
      await qc.invalidateQueries({ queryKey: queryKeys.customersListPrefix(jwt) });
      setSaveOk(true);
    } catch {
      // Pesan error dari updateMut.isError.
    }
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: 560, pt: 1 }}>
      <Typography variant="subtitle2">Data pelanggan</Typography>
      <Typography variant="body2" color="text.secondary">
        Perbarui nama dan alamat jika data pelanggan berbeda.
      </Typography>
      <TextField
        label="Nama pelanggan"
        value={name}
        onChange={(e) => {
          setSaveOk(false);
          setName(e.target.value);
        }}
        required
        fullWidth
        error={nameMissing}
        helperText={nameMissing ? 'Nama pelanggan wajib diisi.' : undefined}
        inputProps={{ 'data-testid': 'survey-detail-customer-name' }}
      />
      <TextField
        label="Alamat"
        value={address}
        onChange={(e) => {
          setSaveOk(false);
          setAddress(e.target.value);
        }}
        fullWidth
        multiline
        minRows={2}
        inputProps={{ 'data-testid': 'survey-detail-customer-address' }}
      />
      <TextField
        label="Kota"
        value={city}
        onChange={(e) => {
          setSaveOk(false);
          setCity(e.target.value);
        }}
        fullWidth
        inputProps={{ 'data-testid': 'survey-detail-customer-city' }}
      />
      <TextField
        label="Provinsi"
        value={province}
        onChange={(e) => {
          setSaveOk(false);
          setProvince(e.target.value);
        }}
        fullWidth
        inputProps={{ 'data-testid': 'survey-detail-customer-province' }}
      />
      <TextField
        label="Kode pos"
        value={postalCode}
        onChange={(e) => {
          setSaveOk(false);
          setPostalCode(e.target.value);
        }}
        fullWidth
        inputProps={{ 'data-testid': 'survey-detail-customer-postal-code' }}
      />
      <TextField
        label="Nama kontak"
        value={contactName}
        onChange={(e) => {
          setSaveOk(false);
          setContactName(e.target.value);
        }}
        fullWidth
        inputProps={{ 'data-testid': 'survey-detail-customer-contact-name' }}
      />
      <TextField
        label="Jabatan"
        value={jobTitle}
        onChange={(e) => {
          setSaveOk(false);
          setJobTitle(e.target.value);
        }}
        fullWidth
        inputProps={{ 'data-testid': 'survey-detail-customer-job-title' }}
      />
      {updateMut.isError ? (
        <Alert severity="error">
          {updateMut.error instanceof Error
            ? updateMut.error.message
            : 'Gagal menyimpan data pelanggan.'}
        </Alert>
      ) : null}
      {saveOk ? <Alert severity="success">Data pelanggan berhasil disimpan.</Alert> : null}
      <Button
        variant="contained"
        onClick={() => {
          void onSave();
        }}
        disabled={!dirty || nameMissing || updateMut.isPending}
        data-testid="survey-detail-customer-address-save"
      >
        {updateMut.isPending ? 'Menyimpan…' : 'Simpan pelanggan'}
      </Button>
    </Stack>
  );
}
